import gql from 'graphql-tag';
import { set } from 'lodash';
import { format } from 'date-fns';
import { apolloClient } from '@/vue-apollo';
import querify from '@/utils/graphqlQuerify';

function buildWhere(model, searchTerm, selectedCars) {
  const obj = {};
  let dateFilter = null;
  set(obj, 'brand.validated._eq', true);
  set(obj, 'id._in', selectedCars);

  const {
    start,
    end,
  } = model.productionDates;
  if (start && end) {
    dateFilter = [
      { eop: { _gte: format(start, 'y-MM-dd'), _lte: format(end, 'y-MM-dd') } },
      { sop: { _lte: format(end, 'y-MM-dd'), _gte: format(start, 'y-MM-dd') } },
    ];
  } else if (start) {
    set(obj, 'sop._gte', format(start, 'y-MM-dd'));
  } else if (end) {
    set(obj, 'eop._lte', format(end, 'y-MM-dd'));
  }

  if (model.oem.length) {
    set(obj, 'brand.automaker.name._in', model.oem);
  }

  if (model.oemBrand.length) {
    set(obj, 'brand.name._in', model.oemBrand);
  }

  if (model.oemProductionRegion.length) {
    set(obj, 'country.region._in', model.oemProductionRegion);
  }

  if (model.oemProductionCountry.length) {
    set(obj, 'country.name._in', model.oemProductionCountry);
  }

  if (model.segment.length) {
    set(obj, 'segment._in', model.segment);
  }

  if (model.body.length) {
    set(obj, 'body._in', model.body);
  }

  if (model.model.length) {
    set(obj, 'model._in', model.model);
  }

  if (model.powerTrain.length) {
    set(obj, 'power_train._in', model.powerTrain);
  }

  if (model.generation.length) {
    set(obj, 'generation._in', model.generation);
  }

  if (model.phase.length) {
    set(obj, 'phase._in', model.phase);
  }

  if (searchTerm) {
    const likeSearchTerm = `%${searchTerm.replaceAll(/"/g, '')}%`;
    const searches = [
      { brand: { name: { _ilike: likeSearchTerm } } },
      { model: { _ilike: likeSearchTerm } },
      { project_code: { _ilike: likeSearchTerm } },
      { segment: { _ilike: likeSearchTerm } },
      { body: { _ilike: likeSearchTerm } },
      { commercial_name: { _ilike: likeSearchTerm } },
    ];

    if (dateFilter) {
      set(obj, '_and', [{ _or: searches }, { _or: dateFilter }]);
    } else {
      set(obj, '_or', searches);
    }
  } else if (dateFilter) {
    set(obj, '_or', dateFilter);
  }

  return querify(obj);
}

async function getDynamicOptions(model = {}, searchTerm, changedField, selectedCars) {
  const where = buildWhere(model, searchTerm, selectedCars);

  const filteredData = await apolloClient.query({
    query: gql`query {

      cars(
        where: ${where},
      ) {
        id
        model
        project_code
        segment
        body
        generation
        phase
        production_type
        sop
        eop
        commercial_name
        power_train
        front_image
        back_image
        brand {
          automaker {
            name
          }
          name
          logo
        }
        country {
          region
          name
          code
        }
      }
    }`,
  });

  const filteredDataInColumn = this.getModel();
  filteredData.data.cars.map((record) => {
    filteredDataInColumn.oem.push(record.brand.automaker.name);
    filteredDataInColumn.oemBrand.push(record.brand.name);
    filteredDataInColumn.oemProductionRegion.push(record.country.region);
    filteredDataInColumn.oemProductionCountry.push(record.country.name);
    filteredDataInColumn.segment.push(record.segment);
    filteredDataInColumn.body.push(record.body);
    filteredDataInColumn.model.push(record.model);
    filteredDataInColumn.powerTrain.push(record.power_train);
    filteredDataInColumn.generation.push(record.generation);
    filteredDataInColumn.phase.push(record.generation_phase);
    return true;
  });

  const result = {
    oem: [...new Set(filteredDataInColumn.oem)].sort(),
    oemBrand: [...new Set(filteredDataInColumn.oemBrand)].sort(),
    oemProductionRegion: [...new Set(filteredDataInColumn.oemProductionRegion)].sort(),
    oemProductionCountry: [...new Set(filteredDataInColumn.oemProductionCountry)].sort(),
    model: [...new Set(filteredDataInColumn.model)].sort(),
    segment: [...new Set(filteredDataInColumn.segment)].sort(),
    body: [...new Set(filteredDataInColumn.body)].sort(),
    powerTrain: [...new Set(filteredDataInColumn.powerTrain)].sort(),
    generation: [...new Set(filteredDataInColumn.generation)].sort(),
    phase: [...new Set(filteredDataInColumn.phase)].sort(),
  };

  if (changedField.field && changedField.direction === 'add') {
    delete result[changedField.field];
  }

  return result;
}

export default {
  getSchema() {
    return [
      {
        name: 'productionDates',
        type: 'dateRange',
        label: 'Production Dates',
      },
      {
        name: 'oem',
        type: 'select',
        isMultiple: true,
        label: 'OEM',
      },
      {
        name: 'oemBrand',
        type: 'select',
        isMultiple: true,
        label: 'OEM brand',
      },
      {
        name: 'segment',
        type: 'select',
        isMultiple: true,
        label: 'Segment',
      },
      {
        name: 'model',
        type: 'select',
        isMultiple: true,
        label: 'Model',
      },
      {
        name: 'powerTrain',
        type: 'select',
        isMultiple: true,
        label: 'Power train',
      },
      {
        name: 'generation',
        type: 'select',
        isMultiple: true,
        label: 'Generation',
      },
      {
        name: 'phase',
        type: 'select',
        isMultiple: true,
        label: 'Phase',
      },
      {
        name: 'body',
        type: 'select',
        isMultiple: true,
        label: 'Body',
      },
      {
        name: 'oemProductionRegion',
        type: 'select',
        isMultiple: true,
        label: 'OEM production region',
      },
      {
        name: 'oemProductionCountry',
        type: 'select',
        isMultiple: true,
        label: 'OEM production country',
      },
    ];
  },
  getModel() {
    return {
      productionDates: {
        start: null,
        end: null,
      },
      oem: [],
      oemBrand: [],
      oemProductionRegion: [],
      oemProductionCountry: [],
      segment: [],
      body: [],
      model: [],
      powerTrain: [],
      generation: [],
      phase: [],
    };
  },
  getDynamicOptions,
  async getOptions() {
    const result = await apolloClient.query({
      query: gql`query {
        oem: automakers(
          distinct_on: name,
          where: { value_chain_level: { _eq: "OEM", }, },
          order_by: {name: asc},
        ) {
          name
        }

        oemBrand: brands(
          distinct_on: name,
          where: { validated: { _eq: true, }, },
          order_by: {name: asc},
        ) {
          name
        }

        oemProductionRegion: countries(
          distinct_on: region,
          order_by: {region: asc},
        ) {
          region
        }

        oemProductionCountry: countries(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        model: cars(
          distinct_on: model,
          order_by: {model: asc},
        ) {
          model
        }

        segment: cars(
          distinct_on: segment,
          order_by: {segment: asc},
        ) {
          segment
        }

        body: cars(
          distinct_on: body,
          order_by: {body: asc},
        ) {
          body
        }

        powerTrain: cars(
          distinct_on: power_train,
          order_by: {power_train: asc},
        ) {
          power_train
        }

        generation: cars(
          distinct_on: generation,
          order_by: {generation: asc},
        ) {
          generation
        }

        phase: cars(
          distinct_on: phase,
          order_by: {phase: asc},
        ) {
          phase
        }
      }`,
    });

    return {
      oem: result.data.oem.map((x) => x.name),
      oemBrand: result.data.oemBrand.map((x) => x.name),
      oemProductionRegion: result.data.oemProductionRegion.map((x) => x.region),
      oemProductionCountry: result.data.oemProductionCountry.map((x) => x.name),
      model: result.data.model.map((x) => x.model),
      segment: result.data.segment.map((x) => x.segment),
      body: result.data.body.map((x) => x.body),
      powerTrain: result.data.powerTrain.map((x) => x.power_train),
      generation: result.data.generation.map((x) => x.generation),
      phase: result.data.phase.map((x) => x.phase),
    };
  },
  async getData(model, searchTerm, perPage, page, companyUuid, selectedCars) {
    const where = buildWhere(model, searchTerm, selectedCars);
    const offset = (page - 1) * perPage;

    let res = await apolloClient.query({
      query: gql`query {
        cars_aggregate(
          where: ${where},
        ) {
          aggregate {
            count
          }
        }

        cars(
          where: ${where},
          order_by: [{brand: {name: asc}}, {model: asc}, {generation: asc}, {phase: asc}],
          limit: ${perPage},
          offset: ${offset},
        ) {
          id
          model
          project_code
          segment
          body
          generation
          phase
          production_type
          volumes_infos {
            sop: sop_${companyUuid.replace(/-/g, '')}
            eop: eop_${companyUuid.replace(/-/g, '')}
            has_volumes: has_volumes_${companyUuid.replace(/-/g, '')}
          }
          commercial_name
          power_train
          front_image
          back_image
          brand {
            automaker {
              name
            }
            name
            logo
          }
          country {
            region
            name
            code
          }
        }
      }`,
    });

    const total = res.data.cars_aggregate.aggregate.count;
    res = res.data.cars.map((item) => (
      {
        ...item,
        volumes_infos: item.volumes_infos[0],
      }
    )).filter((item) => (item.volumes_infos.has_volumes
        && new Date(item.volumes_infos.eop) >= new Date(2017, 0, 1)));

    return {
      data: res,
      total,
    };
  },
};
